import React from 'react';
import { createPortal } from 'react-dom';
import { Send, Loader, X as XIcon, ChevronDown } from 'lucide-react';
import { useGetRolesQuery } from '../../redux/api/authApi';

export interface InviteFormData {
  email: string;
  role: string;
  expires_in_days: number;
}

interface InviteMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: InviteFormData) => void;
  isLoading?: boolean;
  initialData?: Partial<InviteFormData>;
}

const InviteMemberModal: React.FC<InviteMemberModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading = false,
  initialData,
}) => {
  const { data: roles = [], isLoading: isLoadingRoles } = useGetRolesQuery(undefined, {
    skip: !isOpen, // Only fetch when modal is open
  });

  const [formData, setFormData] = React.useState<InviteFormData>({
    email: initialData?.email || '',
    role: initialData?.role || (roles.length > 0 ? roles[0].value : 'tenant_member'),
    expires_in_days: initialData?.expires_in_days || 7,
  });

  // Update role when roles are loaded
  React.useEffect(() => {
    if (roles.length > 0 && !formData.role) {
      setFormData((prev) => ({ ...prev, role: roles[0].value }));
    }
  }, [roles, formData.role]);

  React.useEffect(() => {
    if (initialData) {
      setFormData((prev) => ({
        ...prev,
        ...initialData,
      }));
    }
  }, [initialData]);

  React.useEffect(() => {
    if (isOpen) {
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const handleClose = () => {
    setFormData({
      email: '',
      role: roles.length > 0 ? roles[0].value : 'tenant_member',
      expires_in_days: 7,
    });
    onClose();
  };

  if (!isOpen) return null;

  const modalContent = (
    <div
      className="fixed inset-0 flex items-center justify-center bg-[var(--color-modal-backdrop)]"
      style={{ zIndex: 99999 }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          handleClose();
        }
      }}
    >
      <div
        className="bg-[var(--color-modal-bg)] border border-[var(--color-border)] rounded-lg p-6 w-full max-w-xl mx-4 relative shadow-xl"
        onClick={(e) => e.stopPropagation()}
        style={{ zIndex: 100000 }}
      >
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] transition-colors"
          disabled={isLoading}
        >
          <XIcon className="w-5 h-5" />
        </button>

        <h3 className="text-xl font-bold text-[var(--color-text-primary)] mb-4">Invite Member</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1">Email *</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="eg-input w-full"
              placeholder="user@example.com"
              required
              disabled={isLoading}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1">Role *</label>
            <div className="relative">
              <select
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                className="eg-select w-full pr-10"
                disabled={isLoading || isLoadingRoles}
              >
                {isLoadingRoles ? (
                  <option>Loading roles...</option>
                ) : roles.length === 0 ? (
                  <>
                    <option value="tenant_member">TENANT_MEMBER</option>
                    <option value="tenant_admin">TENANT_ADMIN</option>
                  </>
                ) : (
                  roles.map((role) => (
                    <option key={role.value} value={role.value}>
                      {role.label}
                    </option>
                  ))
                )}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--color-text-primary)] pointer-events-none" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1">Expires In (Days)</label>
            <input
              type="number"
              value={formData.expires_in_days}
              onChange={(e) => setFormData({ ...formData, expires_in_days: parseInt(e.target.value) || 7 })}
              min="1"
              max="30"
              className="eg-input w-full"
              disabled={isLoading}
            />
          </div>
          <div className="flex gap-3 justify-end">
            <button
              type="button"
              onClick={handleClose}
              disabled={isLoading}
              className="eg-btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="eg-btn-primary flex items-center gap-2"
            >
              {isLoading ? (
                <>
                  <Loader className="animate-spin" size={16} />
                  Sending...
                </>
              ) : (
                <>
                  <Send size={16} />
                  Send Invitation
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  // Render modal using portal to document.body to avoid z-index/overflow issues
  const portalTarget = typeof document !== 'undefined' ? document.body : null;
  
  if (!portalTarget) return null;

  return createPortal(modalContent, portalTarget);
};

export default InviteMemberModal;

